import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { RequestStatusBadge, useWithdrawalRequest } from "@/entities/wallet";
import { ApiError } from "@/shared/api";
import { formatDateTime, formatMoney } from "@/shared/lib";
import { RollingNumber } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Шаг 4: статус заявки на вывод. Каркас `Deposit Pending` / `Deposit Success`
 * (24:171 / 24:209) — DESIGN.md §5.
 *
 * Порядок статусов: `pending` → `approved` (деньги списаны и зарезервированы)
 * → `paid` (администратор проставил хеш выплаты). `rejected` достижим из
 * обоих, и при отклонении уже подтверждённой заявки баланс возвращается.
 */
export function WithdrawStatusPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const withdrawalId = Number(id);

  const { data, isPending, isError, error, refetch } = useWithdrawalRequest(withdrawalId);

  if (!Number.isFinite(withdrawalId)) {
    return (
      <ScreenState tone="info" icon="questionMark" title={t("wallet.notFound.title")} body={t("wallet.notFound.body")} />
    );
  }

  if (isPending) return <ScreenSkeleton blocks={[120, 140]} />;

  if (isError) {
    const notFound = error instanceof ApiError && error.code === "NOT_FOUND";
    return (
      <ScreenState
        tone={notFound ? "info" : "loss"}
        icon={notFound ? "questionMark" : "warningFill"}
        title={t(notFound ? "wallet.notFound.title" : "states.error.title")}
        body={t(notFound ? "wallet.notFound.body" : "states.error.body")}
        {...(notFound ? {} : { action: { label: t("states.error.action"), onClick: () => void refetch() } })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("withdraw.requestTitle")} subtitle={formatDateTime(data.created_at)} />

      <section className="flex flex-col gap-3 rounded-3xl border border-border-subtle bg-background-elevated p-5">
        <div className="flex items-start justify-between">
          <RollingNumber
            value={formatMoney(data.amount, { suffix: data.asset })}
            className="text-h2 font-bold text-text-primary"
          />
          <RequestStatusBadge status={data.status} />
        </div>

        <p className="text-caption font-normal text-text-secondary">
          {t(`withdraw.statusNote.${data.status}`)}
        </p>

        {data.admin_comment ? (
          <p className="text-caption font-normal text-text-secondary">{data.admin_comment}</p>
        ) : null}
      </section>

      <dl className="flex flex-col gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
        <div className="flex items-start justify-between gap-3">
          <dt className="text-caption font-medium text-text-secondary">{t("wallet.network")}</dt>
          <dd className="text-label font-semibold text-text-primary">{data.network}</dd>
        </div>
        <div className="flex items-start justify-between gap-3">
          <dt className="shrink-0 text-caption font-medium text-text-secondary">
            {t("withdraw.addressLabel")}
          </dt>
          <dd className="min-w-0 font-mono text-caption break-all text-text-primary">{data.address}</dd>
        </div>
        {data.tx_hash ? (
          <div className="flex items-start justify-between gap-3">
            <dt className="shrink-0 text-caption font-medium text-text-secondary">
              {t("wallet.txHash")}
            </dt>
            <dd className="min-w-0 font-mono text-caption break-all text-text-primary">
              {data.tx_hash}
            </dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
